import { PaletteLight } from '@repo/go-icons';
import { Button, Scrollbar } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Outlet, useNavigate, useParams } from 'react-router-dom';

import { ROUTES } from 'src/shared/constants/routes';
import { MainLayout } from 'src/shared/layouts/main-layout/main-layout';
import { useStore } from 'src/store';

import { PaletteSettings } from './features';
import { PaletteSettingsStore } from './features/palette-settings/palette-settings.store';
import styles from './settings-page.module.scss';
import type { TSection, TSubSection } from './types';

export const SettingsPage = observer(function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { directories, systemSettings, notifications } = useStore();
  const { section: paramsSection, subsection: paramsSubSection, '*': splat } = useParams();
  const [paletteSettingsStore] = useState(() => new PaletteSettingsStore(directories, systemSettings, notifications));

  const sections: TSection[] = useMemo(() => {
    return [
      {
        id: 'palette',
        name: t('systemSettings:Sections.palette'),
        icon: <PaletteLight />,
        render: <PaletteSettings store={paletteSettingsStore} />,
        onClick: () => {
          if (systemSettings.section === 'palette') {
            return;
          }

          navigate('palette');
        },
      },
    ];
  }, [t, paletteSettingsStore, systemSettings.section, navigate]);

  const subSections: Record<string, TSubSection[]> = useMemo(() => {
    return {
      palette: paletteSettingsStore.subSections.map(({ id, name: tName }) => {
        return {
          id,
          name: t(tName),
          onClick: () =>
            navigate(
              generatePath(ROUTES['settings/section/subsection'], {
                section: paramsSection ?? null,
                subsection: id,
              }),
            ),
        };
      }),
    };
  }, [paletteSettingsStore.subSections, t, navigate, paramsSection]);

  const settingsBlock = useMemo(() => {
    const section = sections.find((s) => s.id === systemSettings.section);
    const sectionRender = section?.render;

    if (!section) {
      return null;
    }

    if (sectionRender) {
      return sectionRender;
    }

    const subSection = subSections[section.id]?.find((ss) => ss.id === systemSettings.subSection);

    return subSection?.render ?? null;
  }, [sections, subSections, systemSettings.section, systemSettings.subSection]);

  useEffect(() => {
    systemSettings.setSection(paramsSection ?? null);
  }, [paramsSection, systemSettings]);

  useEffect(() => {
    systemSettings.setSubSection(paramsSubSection ?? null);
  }, [paramsSubSection, systemSettings]);

  useEffect(() => {
    if (splat) {
      navigate(
        generatePath(ROUTES['settings/section/subsection'], {
          section: paramsSection ?? null,
          subsection: paramsSubSection ?? null,
        }),
        { replace: true },
      );
    }

    const hasCurrentSection = sections.find((s) => s.id === paramsSection);

    if (!systemSettings.section) {
      return;
    }

    if (!hasCurrentSection && !!systemSettings.section) {
      navigate(ROUTES.settings);
      return;
    }

    const subSection =
      subSections[systemSettings.section]?.find((ss) => ss.id === paramsSubSection)?.id ??
      subSections[systemSettings.section]?.[0]?.id;

    systemSettings.setSubSection(subSection);
    navigate(
      generatePath(ROUTES['settings/section/subsection'], {
        section: paramsSection ?? null,
        subsection: subSection ?? null,
      }),
      { replace: true },
    );
    return;
  }, [
    paramsSection,
    paramsSubSection,
    sections,
    splat,
    subSections,
    systemSettings.section,
    systemSettings.subSection,
    navigate,
    systemSettings,
  ]);

  return (
    <MainLayout title={t('labels:systemSettings')} sidebarWithoutClosedState>
      <div className={styles.contentWrapper}>
        <ul className={styles.sections}>
          <Scrollbar>
            {sections.map((section) => {
              const isActive = systemSettings.section === section.id;

              return (
                <Button
                  key={section.id}
                  icon={section.icon}
                  className={clsx(styles.sectionButton, isActive && styles.sectionButton__active)}
                  onClick={section.onClick}
                >
                  {section.name}
                </Button>
              );
            })}
          </Scrollbar>
        </ul>
        {!!systemSettings.section && subSections[systemSettings.section]?.length && (
          <ul className={styles.subSections}>
            <Scrollbar>
              {subSections[systemSettings.section].map((ss) => {
                const isActive = systemSettings.subSection === ss.id;

                return (
                  <Button
                    key={ss.id}
                    className={clsx(styles.subSectionButton, isActive && styles.subSectionButton__active)}
                    onClick={ss.onClick}
                  >
                    {ss.name}
                  </Button>
                );
              })}
            </Scrollbar>
          </ul>
        )}
        {settingsBlock}
        <Outlet />
      </div>
    </MainLayout>
  );
});
